import { Button } from "components/DesignSystem/Button/Button";
import Modal from "components/DesignSystem/Modal/Modal";
import { Stepper } from "components/DesignSystem/Stepper/Stepper";
import { SmallRightArrow } from "components/icons/SmallRightArrow";
import {
  RND_ONBOARDING_STEP_BY_INDEX,
  RND_ONBOARDING_STEP_INDEX,
} from "constants/financialClosing";
import { useEffect, useRef } from "react";
import {
  useGetRnDBifurcationQuery,
  useUpdateRnDOnboardingStepMutation,
} from "store/apis/financialClosing";

export type RnDOnboardingState = {
  isOnboardingVisible: boolean;
  currentSlide: OnboardingSlideIndex;
  isNavigating: boolean;
  dismiss: () => void;
  back: () => void;
  advance: () => void;
  revisit: () => void;
};

export const useRnDOnboardingState = (
  entityId: string,
  financialClosingId: string,
  isTriggered: boolean
): RnDOnboardingState => {
  const { data } = useGetRnDBifurcationQuery(
    { entityId, financialClosingId },
    { skip: !entityId || !financialClosingId || !isTriggered }
  );
  const [updateOnboardingStep, { isLoading: isNavigating }] =
    useUpdateRnDOnboardingStepMutation();

  const serverStep = data?.rnd_onboarding_step ?? 0;
  const isCompleted = serverStep === RND_ONBOARDING_STEP_INDEX.COMPLETED;
  const currentSlide = (isCompleted ? 0 : serverStep) as OnboardingSlideIndex;

  const setStep = (step: number) => {
    if (isNavigating) return;
    const stepLabel =
      RND_ONBOARDING_STEP_BY_INDEX[
        step as keyof typeof RND_ONBOARDING_STEP_BY_INDEX
      ];
    void updateOnboardingStep({
      entityId,
      financialClosingId,
      payload: { step: stepLabel },
    });
  };

  return {
    isOnboardingVisible: isTriggered && !isCompleted,
    currentSlide,
    isNavigating,
    dismiss: () => setStep(RND_ONBOARDING_STEP_INDEX.COMPLETED),
    back: () => {
      if (currentSlide > RND_ONBOARDING_STEP_INDEX.INTRO)
        setStep(currentSlide - 1);
    },
    advance: () => {
      if (currentSlide < RND_ONBOARDING_STEP_INDEX.HOW_TO_DO_IT)
        setStep(currentSlide + 1);
    },
    revisit: () => setStep(RND_ONBOARDING_STEP_INDEX.INTRO),
  };
};

type OnboardingSlideIndex = 0 | 1 | 2 | 3;

const MODAL_TITLE = "Bifurcate R&D Expenses";

const STEP_LABELS = ["Why this matters", "How it works", "How to do it"];

const IntroSlide = () => (
  <div className="t-flex t-flex-col t-gap-3">
    <span className="t-text-body-sm t-text-text-30 t-uppercase">
      Before you start
    </span>
    <h2 className="t-text-h4 t-text-text-100 t-m-0">
      What is Research and Development expense bifurcation?
    </h2>
    <p className="t-text-body t-text-text-30 t-m-0">
      Certain R&D costs, such as software and engineering expenses, must be
      capitalized and deducted over several years instead of all at once. We've
      identified vendors that may be used for doing research and development
      work (Engineering/Design/Product). You will have to review and bifurcate
      the expense into onshore US and non-US R&D.
    </p>
  </div>
);

const WhyThisMattersStep = () => (
  <div className="t-flex t-flex-col t-gap-2">
    <h3 className="t-text-h4 t-text-text-100 t-m-0">Why this matters</h3>
    <p className="t-text-body t-text-text-30 t-m-0">
      Non-US R&D expenses have to be capitalized and then amortized for 15
      years. Correctly classifying each vendor helps us prepare your tax filing
      accurately and calculate the tax applicability.
    </p>
  </div>
);

const HOW_IT_WORKS_STEPS = [
  "We identify vendors you might have used for research and development.",
  "You bifurcate where the work happened & how much of the overall expense was for research and development.",
  "We apply the IRS rules and show your taxable position on screen.",
];

const HowItWorksStep = () => (
  <div className="t-flex t-flex-col t-gap-2">
    <h3 className="t-text-h4 t-font-bold t-text-text-100 t-m-0">
      How it works
    </h3>
    <ol className="t-flex t-flex-col t-gap-2 t-list-none t-m-0 t-p-0">
      {HOW_IT_WORKS_STEPS.map((step, index) => (
        <li key={step} className="t-flex t-items-start t-gap-2">
          <span className="t-flex t-items-center t-justify-center t-h-5 t-w-5 t-shrink-0 t-rounded-full t-bg-neutral-0 t-text-caption t-text-text-60">
            {index + 1}
          </span>
          <span className="t-text-body t-text-text-60">{step}</span>
        </li>
      ))}
    </ol>
  </div>
);

const HOW_TO_DO_IT_STEPS = [
  "Was the work done in the US or elsewhere? (US R&D does not need to be amortized)",
  "If it's a non-US vendor, what percentage of the work was related to research and development?",
];

const HowToDoItStep = () => (
  <div className="t-flex t-flex-col t-gap-2">
    <h3 className="t-text-h4 t-font-bold t-text-text-100 t-m-0">
      What we will ask, per vendor
    </h3>
    <ol className="t-flex t-flex-col t-gap-2 t-list-none t-m-0 t-p-0">
      {HOW_TO_DO_IT_STEPS.map((step, index) => (
        <li key={step} className="t-flex t-items-start t-gap-2">
          <span className="t-flex t-items-center t-justify-center t-h-5 t-w-5 t-shrink-0 t-rounded-full t-bg-neutral-0 t-text-caption t-text-text-60">
            {index + 1}
          </span>
          <span className="t-text-body t-text-text-60">{step}</span>
        </li>
      ))}
    </ol>
  </div>
);

const STEP_SLIDES = [WhyThisMattersStep, HowItWorksStep, HowToDoItStep];

const OnboardingSidebar = ({ currentStep }: { currentStep: 0 | 1 | 2 }) => (
  <div className="t-w-52 t-shrink-0 t-border-0 t-border-r t-border-solid t-border-neutral-10 t-pr-1">
    <Stepper size="small" direction="vertical">
      {STEP_LABELS.map((label, index) => (
        <Stepper.Step
          key={label}
          step={index + 1}
          isActive={index === currentStep}
          isCompleted={index < currentStep}
          isPending={index > currentStep}
          notLastStep={index < STEP_LABELS.length - 1}
        >
          {label}
        </Stepper.Step>
      ))}
    </Stepper>
  </div>
);

type OnboardingFooterProps = {
  currentSlide: OnboardingSlideIndex;
  isNavigating: boolean;
  onBack: () => void;
  onSkip: () => void;
  onAdvance: () => void;
  onComplete: () => void;
};

const OnboardingFooter = ({
  currentSlide,
  isNavigating,
  onBack,
  onSkip,
  onAdvance,
  onComplete,
}: OnboardingFooterProps) => {
  const isIntroSlide = currentSlide === 0;
  const isLastSlide = currentSlide === RND_ONBOARDING_STEP_INDEX.HOW_TO_DO_IT;

  if (isIntroSlide) {
    return (
      <div className="t-flex t-items-center t-gap-8">
        <Button
          type="button"
          customType="primary"
          size="regular"
          isLoading={isNavigating}
          rightIcon={<SmallRightArrow width={16} height={16} />}
          onClick={onAdvance}
        >
          Continue
        </Button>
        <Button
          type="button"
          customType="link_neutral"
          disabled={isNavigating}
          onClick={onSkip}
        >
          Skip guide
        </Button>
      </div>
    );
  }

  return (
    <div className="t-flex t-items-center t-justify-between t-w-full">
      <Button
        type="button"
        customType="link_neutral"
        disabled={isNavigating}
        onClick={onSkip}
      >
        Skip guide
      </Button>
      <div className="t-flex t-items-center t-gap-3">
        <Button
          type="button"
          customType="secondary"
          disabled={isNavigating}
          leftIcon={
            <SmallRightArrow width={16} height={16} className="t-rotate-180" />
          }
          onClick={onBack}
        >
          Go back
        </Button>
        <Button
          type="button"
          customType="primary"
          isLoading={isNavigating}
          rightIcon={<SmallRightArrow width={16} height={16} />}
          onClick={isLastSlide ? onComplete : onAdvance}
        >
          {isLastSlide ? "Get started" : "Next"}
        </Button>
      </div>
    </div>
  );
};

type RnDOnboardingProps = {
  onboarding: RnDOnboardingState;
  onDismiss: () => void;
};

export const RnDOnboarding = ({
  onboarding,
  onDismiss,
}: RnDOnboardingProps) => {
  const headingRef = useRef<HTMLDivElement>(null);
  const {
    isOnboardingVisible,
    currentSlide,
    isNavigating,
    dismiss,
    back,
    advance,
  } = onboarding;

  useEffect(() => {
    headingRef.current?.focus({ preventScroll: true });
  }, [currentSlide]);

  if (!isOnboardingVisible) {
    return null;
  }

  const handleDismiss = () => {
    dismiss();
    onDismiss();
  };

  const isIntroSlide = currentSlide === 0;
  const StepSlide = STEP_SLIDES[currentSlide === 0 ? 0 : currentSlide - 1];

  return (
    <>
      <Modal.Root open={isIntroSlide} onOpenChange={handleDismiss}>
        <Modal.Content size="large">
          <Modal.Header>
            <Modal.Title>{MODAL_TITLE}</Modal.Title>
            <Modal.Close />
          </Modal.Header>
          <Modal.Body>
            <div
              ref={headingRef}
              tabIndex={-1}
              className="t-flex t-flex-col t-gap-6 t-min-h-56"
            >
              <IntroSlide />
              <OnboardingFooter
                currentSlide={currentSlide}
                isNavigating={isNavigating}
                onBack={back}
                onSkip={handleDismiss}
                onAdvance={advance}
                onComplete={handleDismiss}
              />
            </div>
          </Modal.Body>
        </Modal.Content>
      </Modal.Root>

      <Modal.Root open={!isIntroSlide} onOpenChange={handleDismiss}>
        <Modal.Content size="large">
          <Modal.Header>
            <Modal.Title>{MODAL_TITLE}</Modal.Title>
            <Modal.Close />
          </Modal.Header>
          <Modal.Body className="!t-pt-8">
            <div className="t-flex t-gap-8 t-min-h-56">
              <OnboardingSidebar
                currentStep={
                  (currentSlide === 0 ? 0 : currentSlide - 1) as 0 | 1 | 2
                }
              />
              <div
                ref={isIntroSlide ? null : headingRef}
                tabIndex={-1}
                className="t-flex-1"
              >
                <StepSlide />
              </div>
            </div>
          </Modal.Body>
          <Modal.Footer>
            <OnboardingFooter
              currentSlide={currentSlide}
              isNavigating={isNavigating}
              onBack={back}
              onSkip={handleDismiss}
              onAdvance={advance}
              onComplete={handleDismiss}
            />
          </Modal.Footer>
        </Modal.Content>
      </Modal.Root>
    </>
  );
};
